import { useState } from 'react'
import { Outlet, Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { CreateDraftDialog } from '@/components/dialogs/CreateDraftDialog'
import { MergeDraftDialog } from '@/components/dialogs/MergeDraftDialog'
import { RenameDraftDialog } from '@/components/dialogs/RenameDraftDialog'
import { DeleteDraftDialog } from '@/components/dialogs/DeleteDraftDialog'
import {
    Home,
    ChevronDown,
    ChevronRight,
    Settings,
    LogOut,
    FileText,
    LayoutGrid,
    List,
    Scissors,
    Send,
    Users,
    FolderOpen,
    Clapperboard,
    X,
    PanelRightOpen,
    PanelRightClose,
    GitBranch,
    Plus,
    Clock,
    Copy,
    MoreHorizontal,
    Pencil,
    Trash,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import logoSvg from '@/assets/logo.svg'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface Project {
    id: string
    name: string
    description: string | null
    status: string
}

interface Draft {
    id: string
    projectId: string
    parentDraftId: string | null
    name: string
    description: string | null
    status: string
    isDefault: boolean
    createdAt: number
    updatedAt: number
}

// Project aspects
type Aspect = 'planning' | 'production' | 'post-production' | 'delivery'

const ASPECTS = [
    { id: 'planning' as Aspect, name: 'Planning', icon: FileText },
    { id: 'production' as Aspect, name: 'Production', icon: Clapperboard },
    { id: 'post-production' as Aspect, name: 'Post-Production', icon: Scissors },
    { id: 'delivery' as Aspect, name: 'Delivery', icon: Send },
]

// Planning tabs - Script/Beats only
const PLANNING_TABS = [
    { id: 'script', name: 'Script', icon: FileText },
    { id: 'beats', name: 'Beats', icon: LayoutGrid },
    { id: 'outline', name: 'Outline', icon: List },
]

// Left sidebar navigation items per aspect
const ASPECT_NAV: Record<Aspect, { name: string; icon: typeof FileText; path: string }[]> = {
    planning: [
        { name: 'Editor', icon: FileText, path: '' },
        { name: 'Characters', icon: Users, path: 'characters' },
        { name: 'Locations', icon: FolderOpen, path: 'locations' },
    ],
    production: [
        { name: 'Coming Soon', icon: Clapperboard, path: '' },
    ],
    'post-production': [
        { name: 'Coming Soon', icon: Scissors, path: '' },
    ],
    delivery: [
        { name: 'Coming Soon', icon: Send, path: '' },
    ],
}

export default function ProjectLayout() {
    const { projectId } = useParams<{ projectId: string }>()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { user, logout } = useAuthStore()

    const [currentAspect, setCurrentAspect] = useState<Aspect>('planning')
    const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false)
    const [rightSidebarOpen, setRightSidebarOpen] = useState(false)

    // Current tab from URL (default: script)
    const currentTab = searchParams.get('tab') || 'script'

    // Fetch project data
    const { data: projectData } = useQuery({
        queryKey: ['project', projectId],
        queryFn: () => api.get<Project>(`/projects/${projectId}`),
        enabled: !!projectId,
    })

    // Fetch drafts
    const { data: draftsData } = useQuery({
        queryKey: ['drafts', projectId],
        queryFn: () => api.get<Draft[]>(`/projects/${projectId}/drafts`),
        enabled: !!projectId,
    })

    const drafts = draftsData?.data || []
    const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null)

    // Dialog states
    const [createDraftOpen, setCreateDraftOpen] = useState(false)
    const [mergeDraftOpen, setMergeDraftOpen] = useState(false)
    const [renameDraftOpen, setRenameDraftOpen] = useState(false)
    const [deleteDraftOpen, setDeleteDraftOpen] = useState(false)
    const [draftToMerge, setDraftToMerge] = useState<Draft | null>(null)
    const [draftToRename, setDraftToRename] = useState<Draft | null>(null)
    const [draftToDelete, setDraftToDelete] = useState<Draft | null>(null)
    const [parentDraftForCreate, setParentDraftForCreate] = useState<{ id: string; name: string } | undefined>(undefined)

    // Derived state
    const project = projectData?.data
    // If selectedDraftId is set, find it. Else find match from URL params.
    const urlDraftId = useParams<{ draftId?: string }>().draftId

    // Determine the effective selected draft ID
    const effectiveDraftId = selectedDraftId || urlDraftId || drafts.find(d => d.isDefault)?.id || drafts[0]?.id

    const selectedDraft = drafts.find(d => d.id === effectiveDraftId)

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    const handleTabClick = (tabId: string) => {
        setSearchParams({ tab: tabId })
    }

    const handleDraftChange = (draftId: string) => {
        setSelectedDraftId(draftId)
        navigate(`/projects/${projectId}/drafts/${draftId}?tab=${currentTab}`)
    }

    const handleCreateDraft = (parent?: { id: string; name: string }) => {
        setParentDraftForCreate(parent)
        setCreateDraftOpen(true)
    }

    const handleMergeDraft = (draft: Draft) => {
        setDraftToMerge(draft)
        setMergeDraftOpen(true)
    }

    const handleRenameDraft = (draft: Draft) => {
        setDraftToRename(draft)
        setRenameDraftOpen(true)
    }

    const handleDeleteDraft = (draft: Draft) => {
        setDraftToDelete(draft)
        setDeleteDraftOpen(true)
    }

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        })
    }

    return (
        <div className="flex h-screen bg-background">
            {/* Left Sidebar - Collapsible */}
            <aside
                className={cn(
                    "flex flex-col border-r bg-card transition-all duration-200",
                    leftSidebarCollapsed ? "w-14" : "w-56"
                )}
            >
                {/* Logo */}
                <div className={cn(
                    "flex h-12 items-center border-b",
                    leftSidebarCollapsed ? "justify-center px-2" : "px-4"
                )}>
                    <Link to="/" className="flex items-center gap-2">
                        <img src={logoSvg} alt="Keyframes" className="h-8 w-8 flex-shrink-0" />
                        {!leftSidebarCollapsed && (
                            <span className="text-lg font-bold">Keyframes</span>
                        )}
                    </Link>
                </div>

                {/* Aspect Selector */}
                <div className={cn("p-2 border-b", leftSidebarCollapsed && "px-1")}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className={cn(
                                "flex w-full items-center gap-2 rounded-lg transition-colors hover:bg-accent",
                                leftSidebarCollapsed ? "justify-center p-2" : "px-3 py-2"
                            )}>
                                {(() => {
                                    const aspect = ASPECTS.find(a => a.id === currentAspect)
                                    const Icon = aspect?.icon || FileText
                                    return (
                                        <>
                                            <Icon className="h-4 w-4 flex-shrink-0" />
                                            {!leftSidebarCollapsed && (
                                                <>
                                                    <span className="text-sm font-medium flex-1 text-left">{aspect?.name}</span>
                                                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                                                </>
                                            )}
                                        </>
                                    )
                                })()}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuLabel>Switch Aspect</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {ASPECTS.map((aspect) => (
                                <DropdownMenuItem
                                    key={aspect.id}
                                    onClick={() => setCurrentAspect(aspect.id)}
                                    className={cn(currentAspect === aspect.id && "bg-accent")}
                                >
                                    <aspect.icon className="mr-2 h-4 w-4" />
                                    {aspect.name}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-2 space-y-1 overflow-auto">
                    {ASPECT_NAV[currentAspect].map((item) => (
                        <Link
                            key={item.name}
                            to={item.path ? `/projects/${projectId}/${item.path}` : `/projects/${projectId}/edit`}
                            className={cn(
                                "flex items-center gap-3 rounded-lg text-sm font-medium transition-colors",
                                "text-muted-foreground hover:bg-accent hover:text-foreground",
                                leftSidebarCollapsed ? "justify-center p-2" : "px-3 py-2"
                            )}
                            title={leftSidebarCollapsed ? item.name : undefined}
                        >
                            <item.icon className="h-4 w-4 flex-shrink-0" />
                            {!leftSidebarCollapsed && item.name}
                        </Link>
                    ))}
                </nav>

                {/* Collapse toggle + User */}
                <div className="border-t p-2">
                    <button
                        onClick={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
                        className={cn(
                            "flex items-center gap-2 w-full rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors mb-2",
                            leftSidebarCollapsed ? "justify-center p-2" : "px-3 py-2"
                        )}
                    >
                        {leftSidebarCollapsed ? (
                            <ChevronRight className="h-4 w-4" />
                        ) : (
                            <>
                                <ChevronRight className="h-4 w-4 rotate-180" />
                                <span className="text-xs">Collapse</span>
                            </>
                        )}
                    </button>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className={cn(
                                "flex w-full items-center gap-2 rounded-lg hover:bg-accent transition-colors",
                                leftSidebarCollapsed ? "justify-center p-2" : "px-2 py-2"
                            )}>
                                <Avatar className="h-7 w-7 flex-shrink-0">
                                    <AvatarImage src={user?.avatarUrl || undefined} />
                                    <AvatarFallback className="text-xs">
                                        {user?.name ? getInitials(user.name) : '??'}
                                    </AvatarFallback>
                                </Avatar>
                                {!leftSidebarCollapsed && (
                                    <div className="flex-1 text-left min-w-0">
                                        <p className="text-xs font-medium truncate">{user?.name}</p>
                                    </div>
                                )}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                            <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate('/settings')}>
                                <Settings className="mr-2 h-4 w-4" />
                                Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                                <LogOut className="mr-2 h-4 w-4" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="h-12 border-b bg-card/50 backdrop-blur flex items-center px-4 gap-4">
                    {/* Left: Home + Project */}
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => navigate('/')}
                        >
                            <Home className="h-4 w-4" />
                        </Button>
                        <span className="text-sm font-medium truncate max-w-[200px]">
                            {project?.name || 'Loading...'}
                        </span>
                    </div>

                    {/* Center: Tabs */}
                    <div className="flex-1 flex items-center justify-center">
                        <nav className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                            {PLANNING_TABS.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabClick(tab.id)}
                                    className={cn(
                                        "px-4 py-1.5 text-sm font-medium rounded-md transition-all",
                                        currentTab === tab.id
                                            ? "bg-background text-foreground shadow-sm"
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Right: Draft indicator + toggle */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-muted/50 hover:bg-muted transition-colors"
                        >
                            <GitBranch className="h-3.5 w-3.5" />
                            <span className="font-medium">{selectedDraft?.name}</span>
                            {rightSidebarOpen ? (
                                <PanelRightClose className="h-3.5 w-3.5 text-muted-foreground" />
                            ) : (
                                <PanelRightOpen className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                        </button>
                    </div>
                </header>

                {/* Content area with optional right sidebar */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Main content */}
                    <main className="flex-1 overflow-auto">
                        <Outlet context={{ currentTab, selectedDraftId: effectiveDraftId, project }} />
                    </main>

                    {/* Right Sidebar - Drafts */}
                    <aside
                        className={cn(
                            "border-l bg-card overflow-hidden transition-all duration-200",
                            rightSidebarOpen ? "w-72" : "w-0"
                        )}
                    >
                        <div className="w-72 h-full flex flex-col">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b">
                                <h3 className="text-sm font-semibold">Drafts</h3>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRightSidebarOpen(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>

                            {/* Draft list */}
                            <div className="flex-1 overflow-auto p-3 space-y-2">
                                {drafts.map((draft) => (
                                    <div
                                        key={draft.id}
                                        className={cn(
                                            "flex items-center group rounded-lg border transition-all",
                                            selectedDraftId === draft.id
                                                ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                                                : "border-border hover:border-muted-foreground/50 hover:bg-accent/50"
                                        )}
                                    >
                                        <button
                                            onClick={() => handleDraftChange(draft.id)}
                                            className="flex-1 text-left p-3 min-w-0 outline-none"
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <GitBranch className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                                <span className="text-sm font-medium truncate">{draft.name}</span>
                                                {draft.isDefault && (
                                                    <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded flex-shrink-0">
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="h-3 w-3" />
                                                {formatDate(draft.updatedAt)}
                                            </div>
                                        </button>

                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 mr-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleRenameDraft(draft)}>
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    Rename
                                                </DropdownMenuItem>

                                                {draft.parentDraftId && (
                                                    <DropdownMenuItem onClick={() => handleMergeDraft(draft)}>
                                                        <GitBranch className="mr-2 h-4 w-4 rotate-180" />
                                                        Merge into Parent
                                                    </DropdownMenuItem>
                                                )}

                                                {!draft.isDefault && (
                                                    <>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            onClick={() => handleDeleteDraft(draft)}
                                                            className="text-destructive focus:text-destructive"
                                                        >
                                                            <Trash className="mr-2 h-4 w-4" />
                                                            Delete
                                                        </DropdownMenuItem>
                                                    </>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                ))}
                            </div>

                            {/* Actions */}
                            <div className="p-3 border-t space-y-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="w-full justify-start gap-2 bg-primary/10 text-primary hover:bg-primary/20"
                                    onClick={() => selectedDraft && handleCreateDraft({ id: selectedDraft.id, name: selectedDraft.name })}
                                    disabled={!selectedDraft}
                                >
                                    <Copy className="h-3.5 w-3.5" />
                                    Branch from Current
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full justify-start gap-2 text-muted-foreground"
                                    onClick={() => handleCreateDraft()}
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    New Blank Draft
                                </Button>

                                {selectedDraft && selectedDraft.parentDraftId && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="w-full justify-start gap-2 bg-primary/10 text-primary hover:bg-primary/20"
                                        onClick={() => handleMergeDraft(selectedDraft)}
                                    >
                                        <GitBranch className="h-3.5 w-3.5 rotate-180" />
                                        Merge into Parent
                                    </Button>
                                )}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            {/* Dialogs */}
            {projectId && (
                <>
                    <CreateDraftDialog
                        open={createDraftOpen}
                        onOpenChange={setCreateDraftOpen}
                        projectId={projectId}
                        parentDraftId={parentDraftForCreate?.id}
                        parentDraftName={parentDraftForCreate?.name}
                        onSuccess={(newDraftId) => handleDraftChange(newDraftId)}
                    />

                    {draftToMerge && draftToMerge.parentDraftId && (
                        <MergeDraftDialog
                            open={mergeDraftOpen}
                            onOpenChange={setMergeDraftOpen}
                            projectId={projectId}
                            draftId={draftToMerge.id}
                            draftName={draftToMerge.name}
                            targetDraftId={draftToMerge.parentDraftId}
                            targetDraftName={drafts.find(d => d.id === draftToMerge.parentDraftId)?.name || 'Parent Draft'}
                            onSuccess={() => {
                                // Switch to parent draft after merge
                                if (draftToMerge.parentDraftId) {
                                    handleDraftChange(draftToMerge.parentDraftId)
                                }
                            }}
                        />
                    )}

                    {draftToRename && (
                        <RenameDraftDialog
                            open={renameDraftOpen}
                            onOpenChange={setRenameDraftOpen}
                            projectId={projectId}
                            draftId={draftToRename.id}
                            currentName={draftToRename.name}
                        />
                    )}

                    {draftToDelete && (
                        <DeleteDraftDialog
                            open={deleteDraftOpen}
                            onOpenChange={setDeleteDraftOpen}
                            projectId={projectId}
                            draftId={draftToDelete.id}
                            draftName={draftToDelete.name}
                            onSuccess={() => {
                                // If we deleted the currently selected draft, switch to default
                                if (selectedDraftId === draftToDelete.id) {
                                    const defaultDraft = drafts.find(d => d.isDefault) || drafts[0]
                                    if (defaultDraft) {
                                        handleDraftChange(defaultDraft.id)
                                    } else {
                                        setSelectedDraftId(null)
                                        navigate(`/projects/${projectId}`)
                                    }
                                }
                            }}
                        />
                    )}
                </>
            )}
        </div>
    )
}
